import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { weeklyAttendance } from '@/mocks/weeklyAttendance';

export default function WeeklyAttendanceChart() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const todayStr = new Date().toISOString().split('T')[0];

  const maxTotal = useMemo(() => Math.max(...weeklyAttendance.map((d) => d.total), 1), []);

  const avgPresent = Math.round(
    weeklyAttendance.reduce((sum, d) => sum + d.present, 0) / weeklyAttendance.length
  );
  const avgPercent = Math.round((avgPresent / maxTotal) * 100);

  return (
    <div className="bg-background-50 rounded-xl border border-background-200 p-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
            <i className="ri-bar-chart-2-line text-accent-700 text-base" />
          </div>
          <div>
            <h3 className="font-heading font-700 text-foreground-900 text-base">{t('dashboard.weeklyAttendanceTrend')}</h3>
            <p className="text-xs text-foreground-400 font-label">{t('dashboard.past7Days')} &middot; {t('dashboard.avgPresent')} {avgPercent}% {t('dashboard.presentPct')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs font-label">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-accent-500" />
            <span className="text-foreground-600">{t('dashboard.present')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm bg-background-300" />
            <span className="text-foreground-600">{t('dashboard.absent')}</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="flex items-end gap-1 sm:gap-3 h-36 sm:h-44">
        {weeklyAttendance.map((day) => {
          const presentHeight = (day.present / maxTotal) * 100;
          const absentHeight = (day.absent / maxTotal) * 100;
          const isToday = day.date === todayStr;
          const rate = Math.round((day.present / day.total) * 100);

          return (
            <div key={day.date} className="flex-1 flex flex-col items-center gap-1.5 min-w-0">
              {/* Rate badge */}
              <span
                className={`text-xs font-label font-700 ${
                  rate >= 90 ? 'text-semantic-success' : rate >= 75 ? 'text-semantic-warning' : 'text-semantic-error'
                }`}
              >
                {rate}%
              </span>

              {/* Bar */}
              <div className="w-full max-w-[48px] flex flex-col justify-end rounded-t-md overflow-hidden" style={{ height: '140px' }}>
                {day.absent > 0 && (
                  <div
                    className="w-full bg-background-200 transition-all duration-500"
                    style={{ height: `${absentHeight}%` }}
                  />
                )}
                <div
                  className={`w-full transition-all duration-500 rounded-t-sm ${
                    isToday ? 'bg-accent-500' : 'bg-accent-300'
                  }`}
                  style={{ height: `${presentHeight}%` }}
                />
              </div>

              {/* Day label */}
              <span
                className={`text-xs font-label font-medium ${
                  isToday ? 'text-accent-700 font-700' : 'text-foreground-500'
                }`}
              >
                {day.day}
              </span>
            </div>
          );
        })}
      </div>

      {/* Summary footer */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mt-4 pt-3 border-t border-background-100">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-foreground-400 font-label">{t('dashboard.avgDailyPresent')}</span>
            <span className="text-xs font-label font-700 text-foreground-800">{avgPresent} / {maxTotal}</span>
          </div>
        </div>
        <button
          onClick={() => navigate('/dashboard/admin/attendance')}
          className="flex items-center gap-1 text-xs font-label font-medium text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          {t('dashboard.viewFullAttendance')}
          <i className="ri-arrow-right-line text-xs" />
        </button>
      </div>
    </div>
  );
}